import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaAward, 
  FaCertificate, 
  FaGraduationCap, 
  FaStar,
  FaQuoteRight,
  FaLinkedin,
  FaEnvelope,
  FaPhone,
  FaArrowRight,
  FaUsers,
  FaTrophy,
  FaBuilding,
  FaGoogle,
  FaStar as FaStarSolid,
  FaMedal,
  FaExternalLinkAlt,
  FaGlobe
} from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 180px);
  background: #ffffff;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5%;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 2rem 5%;
  }
`;

const TrainerSection = styled.div`
  margin-bottom: 4rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 2.5rem;
  color: #1a1a2e;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  span {
    color: #3730A3;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #3730A3, #a78bfa);
    border-radius: 2px;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2rem;
    margin-bottom: 2.5rem;
  }
`;

const TrainerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  align-items: start;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 2rem;
  }
`;

const TrainerImageWrapper = styled.div`
  position: relative;
  animation: ${fadeIn} 1s ease-out;
  max-width: 380px;
  margin: 0 auto;
  margin-top: -20px;

  @media (max-width: 1366px) and (min-width: 769px) {
    max-width: 300px;
    margin-top: -30px;
  }
`;

const TrainerImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 420px;
  object-fit: cover;
  border-radius: 30px;
  box-shadow: 0 30px 50px rgba(55, 48, 163, 0.15);
  border: 4px solid white;
  position: relative;
  z-index: 2;

  @media (max-width: 1366px) and (min-width: 769px) {
    max-height: 340px;
    border-radius: 24px;
    border: 3px solid white;
  }
`;

const TrainerContent = styled.div`
  padding: 1rem 2rem;
  animation: ${fadeIn} 1s ease-out 0.3s both;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.5rem 1rem;
  }
`;

const TrainerName = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 3rem;
  color: #1a1a2e;
  margin-bottom: 0.3rem;
  font-weight: 800;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2.2rem;
  }
`;

const TrainerTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  font-size: 1.3rem;
  color: #3730A3;
  margin-bottom: 1.5rem;
  font-weight: 600;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    padding-bottom: 0.8rem;
  }
`;

const Bio = styled.div`
  font-family: 'Times New Roman', serif;
  margin-bottom: 1.5rem;
  
  p {
    color: #4a4a6a;
    line-height: 1.8;
    font-size: 1.05rem;
    margin-bottom: 1rem;

    @media (max-width: 1366px) and (min-width: 769px) {
      font-size: 0.9rem;
      line-height: 1.6;
      margin-bottom: 0.8rem;
    }
  }
`;

const ContactButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.7rem;
  }
`;

const ContactButton = styled.a`
  font-family: 'Times New Roman', serif;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.6rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.5rem 1.2rem;
    font-size: 0.8rem;
  }
  
  &.linkedin {
    background: #0077b5;
    color: white;
    &:hover { background: #005a8c; transform: translateY(-2px); }
  }
  
  &.email {
    background: #c72a44;
    color: white;
    &:hover { background: #0b0b77; transform: translateY(-2px); }
  }
  
  &.phone {
    background: #1e40af;
    color: white;
    &:hover { background: #1e3a8a; transform: translateY(-2px); }
  }
`;



const TestimonialCard = styled.div`
  font-family: 'Times New Roman', serif;
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(55, 48, 163, 0.05);
  border: 1px solid rgba(55, 48, 163, 0.06);
  margin-top: 3rem;
  position: relative;
  
  .quote-icon {
    position: absolute;
    top: 20px;
    right: 20px;
    color: #3730A3;
    font-size: 3rem;
    opacity: 0.12;
  }
  
  p {
    color: #4a4a6a;
    font-size: 1.05rem;
    line-height: 1.8;
    font-style: italic;
    margin-bottom: 1.5rem;
  }
  
  .student-name {
    color: #1a1a2e;
    font-weight: 600;
    margin-bottom: 0.2rem;
  }
  
  .student-role {
    color: #3730A3;
    font-size: 0.85rem;
    font-weight: 600;
  }
`;

const CTASection = styled.div`
  font-family: 'Times New Roman', serif;
  background: linear-gradient(135deg, #3730A3, #0b0b77);
  border-radius: 24px;
  padding: 3.5rem;
  text-align: center;
  color: white;
  margin-top: 3rem;
  
  h3 {
    font-size: 2.2rem;
    margin-bottom: 0.8rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 1.8rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  font-family: 'Times New Roman', serif;
  background: #ffff;
  color: #3730A3;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: translateY(-3px);
    svg { transform: translateX(5px); }
  }
  
  svg { transition: transform 0.3s ease; }
`;

const OurTrainers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const practopulseServices = [
    { title: "Healthcare IT Solutions", desc: "Innovative technology solutions for healthcare providers" },
    { title: "Practice Management", desc: "Streamlined operations and workflow optimization" },
    { title: "Revenue Cycle Services", desc: "End-to-end RCM solutions" },
    { title: "Coding & Compliance", desc: "Expert coding services with compliance focus" },
    { title: "Telehealth Solutions", desc: "Virtual care platforms" },
    { title: "Data Analytics", desc: "Actionable insights for better decisions" }
  ];

  return (
    <PageContainer>
      <Container>
        <TrainerSection>
          <SectionTitle>
            Meet Our <span>Expert Trainer</span>
          </SectionTitle>
          
          <TrainerGrid>
            <TrainerImageWrapper>
              <TrainerImage 
                src="/images/trainer.png" 
                alt="Gaayatri Jayapal"
              />
            </TrainerImageWrapper>
            
            <TrainerContent>
              <TrainerName>Gaayatri Jayapal</TrainerName>
              <TrainerTitle>Founder & Lead Trainer at Learn Thru Us Academy</TrainerTitle>

              <div style={{ 
                fontFamily: "'Times New Roman', serif", 
                fontSize: '1.1rem', 
                color: '#4a4a6a', 
                marginBottom: '1.5rem',
                letterSpacing: '0.5px'
              }}>
                BSc, E-MBA, CCS-P, CCS, CDIP
              </div>
              
              <Bio>
                <p>
                  Gaayatri Jayapal is a visionary educator with over 17+ years of experience in medical coding 
                  and healthcare education. She developed the revolutionary 'LearnThruUs Methodology' that has 
                  helped thousands of students achieve certification success.
                </p>
              </Bio>
             
              <ContactButtons>
                <ContactButton href="https://www.linkedin.com/in/gaayatrijayapal/" target="_blank" className="linkedin">
                  <FaLinkedin /> LinkedIn
                </ContactButton>
                <ContactButton href="mailto:gaayatri@learnthruus.com" className="email">
                  <FaEnvelope /> Email
                </ContactButton>
                <ContactButton href="tel:+1234567890" className="phone">
                  <FaPhone /> Call
                </ContactButton>
              </ContactButtons>
            </TrainerContent>
          </TrainerGrid>
        </TrainerSection>

        

        <TestimonialCard>
          <FaQuoteRight className="quote-icon" />
          <p>
            "Gaayatri's teaching methodology transformed my understanding of medical coding. 
            Her practical approach and dedication to student success helped me pass my CCS 
            exam on the first attempt. She doesn't just teach coding; she builds careers."
          </p>
          <div className="student-name">— Priya Sharma</div>
          <div className="student-role">CCS Certified, Senior Coder</div>
        </TestimonialCard>

        <CTASection>
          <h3>Learn from the Best</h3>
          <p>Join Gaayatri's classes and experience the LearnThruUs Methodology firsthand</p>
          <CTAButton to="/courses">
            Explore Courses <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default OurTrainers;